import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { useAuth, API } from "../lib/auth";
import { MessageCircle, Trash2, RefreshCw, User, Bot, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function MayaChatHistory() {
  const { authHeader } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [activeMessages, setActiveMessages] = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/chat/sessions?limit=200`, { headers: authHeader });
      setSessions(r.data?.items || []);
    } catch (e) {
      console.error("chat sessions load failed", e);
      toast.error(e?.response?.data?.detail || "Could not load chat sessions — please retry in a moment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openSession = async (s) => {
    setActiveSession(s); setActiveMessages([]); setOpen(true); setActiveLoading(true);
    try {
      const r = await axios.get(`${API}/admin/chat/sessions/${encodeURIComponent(s.session_id)}`, { headers: authHeader });
      setActiveMessages(r.data?.messages || []);
    } catch (e) {
      // Don't block the dialog — show whatever we have and surface a soft warning
      console.error("chat session load failed", e);
      toast.error(e?.response?.data?.detail || "Conversation could not be loaded — it may have been deleted.");
    } finally {
      setActiveLoading(false);
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm("Delete this conversation? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/chat/sessions/${encodeURIComponent(sessionId)}`, { headers: authHeader });
      toast.success("Conversation deleted");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const filtered = sessions.filter((s) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      (s.user_name || "").toLowerCase().includes(needle) ||
      (s.user_email || "").toLowerCase().includes(needle) ||
      (s.last_message || "").toLowerCase().includes(needle) ||
      (s.session_id || "").toLowerCase().includes(needle)
    );
  });

  return (
    <Card className="p-6" data-testid="maya-chat-history">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <MessageCircle className="h-4 w-4" />Maya chat history
        </h3>
        <div className="flex items-center gap-2 flex-1 max-w-md ml-auto">
          <div className="flex items-center gap-2 flex-1 px-3 border border-slate-200 rounded-full">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <Input
              data-testid="chat-history-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email or message…"
              className="border-0 focus-visible:ring-0 shadow-none h-9 text-sm"
            />
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="chat-history-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {loading && sessions.length === 0 ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-500 py-10 text-center">No conversations match your search.</p>
      ) : (
        <div className="divide-y divide-slate-100" data-testid="chat-history-list">
          {filtered.map((s) => (
            <button
              key={s.session_id}
              type="button"
              data-testid={`chat-session-${s.session_id}`}
              onClick={() => openSession(s)}
              className="w-full text-left py-3 px-2 hover:bg-slate-50 rounded-lg transition-colors flex items-start gap-3"
            >
              <div className="h-9 w-9 rounded-full bg-sky-100 text-[#1A8FE3] flex items-center justify-center shrink-0">
                <User className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 truncate">
                    {s.user_name || s.user_email || "Anonymous visitor"}
                  </span>
                  {s.user_role && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                      {s.user_role}
                    </span>
                  )}
                  {s.language && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700">
                      {s.language}
                    </span>
                  )}
                  <span className="text-[10px] text-slate-400 ml-auto shrink-0">
                    {s.last_message_at ? new Date(s.last_message_at).toLocaleString() : ""}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{s.last_message}</p>
                <p className="text-[11px] text-slate-400 mt-1">{s.message_count} message{s.message_count > 1 ? "s" : ""}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="chat-session-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-[#1A8FE3]" />
              Conversation with {activeSession?.user_name || activeSession?.user_email || "Anonymous visitor"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Session {activeSession?.session_id?.slice(0, 8)}… · {activeSession?.user_email || "no email"} · {activeSession?.language || "en"}
            </DialogDescription>
          </DialogHeader>
          {activeLoading ? (
            <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-3" data-testid="chat-session-messages">
              {activeMessages.map((m) => (
                <div key={m.id} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <div className="h-7 w-7 rounded-full bg-sky-100 text-[#1A8FE3] flex items-center justify-center shrink-0 mt-0.5">
                      <Bot className="h-3.5 w-3.5" />
                    </div>
                  )}
                  <div className={`rounded-2xl px-3.5 py-2.5 max-w-[78%] text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#1A8FE3] text-white" : "bg-slate-100 text-slate-800"}`}>
                    {m.text}
                    <div className={`text-[10px] mt-1 ${m.role === "user" ? "text-sky-100/80" : "text-slate-400"}`}>
                      {new Date(m.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                  {m.role === "user" && (
                    <div className="h-7 w-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center shrink-0 mt-0.5">
                      <User className="h-3.5 w-3.5" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {activeSession && (
            <div className="pt-3 border-t border-slate-100 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-rose-600 hover:text-rose-700"
                data-testid="chat-session-delete"
                onClick={() => deleteSession(activeSession.session_id)}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />Delete conversation
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
